import * as React from 'react';
import {useContext} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ForgotPassword from './ForgotPassword';
import {GithubIcon, GoogleIcon} from './CustomIcons';
import OrangeTrieLogo from '../images/orange_trie_logo.svg';
import {emailSignIn, emailSignUp, githubSignIn, googleSignIn} from "../apis/junkyard";
import {EmailManualSignInRequest, GithubSignInPrepResponse, GoogleSignInPrepResponse, LoginType} from "../utils/types";
import {AuthContext} from "./AuthContext";
import {useNavigate} from "react-router";
import {JunkyardCard} from "./JunkyardCard";
import TermsOfServiceDialog from './TermsOfServiceDialog';

export default function SignInCard() {
  const authContext = useContext(AuthContext);
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [termsOpen, setTermsOpen] = React.useState(false);
  const navigate = useNavigate(); // Add this at the component level

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleGithubSignIn = async () => {
    authContext.logout()
    try {
      const signInParams: GithubSignInPrepResponse = await githubSignIn();
      window.location.href = signInParams.full_url;
      window.open(signInParams.full_url, '_self')
    } catch (error) {
      alert(`Failed to sign in! ${error}`)
    }

  }

  const handleGoogleSignIn = async () => {
    authContext.logout()
    try {
      const signInParams: GoogleSignInPrepResponse = await googleSignIn();
      window.location.href = signInParams.full_url;
      window.open(signInParams.full_url, '_self')
    } catch (error) {
      alert(`Failed to sign in! ${error}`)
    }

  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (emailError || passwordError) {
      event.preventDefault();
      return;
    }
    const data = new FormData(event.currentTarget);
    console.log({
      email: data.get('email'),
      password: data.get('password'),
    });
  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleManualSignIn = async (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (event) event.preventDefault(); // prevent full page reload upon failure
    if (!validateInputs()) {
      return
    }
    authContext.logout()

    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    const _response = await emailSignIn({email: email.value, password: password.value} as EmailManualSignInRequest);
    if (_response && _response.account_details && _response.access_token && _response.expires_in) {
      authContext.setCurrentUser(_response.account_details.email_address);
      authContext.storeLoginType(LoginType.email);
      authContext.storeAccessToken(_response.access_token);
      navigate("/");
    } else if (_response && _response.message === "Invalid Password") {
      setPasswordError(true);
      setPasswordErrorMessage("Invalid password.")
    } else {
      setEmailError(true);
      setEmailErrorMessage("Sign in failed with this email address.");
      setPasswordError(true);
      setPasswordErrorMessage("Sign in failed with this password.");
    }
  }

  const handleManualSignUp = async (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault(); // prevent full page reload upon failure
    if (!validateInputs()) {
      return
    }
    authContext.logout()

    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    const _response = await emailSignUp({email: email.value, password: password.value} as EmailManualSignInRequest);
    console.debug("Manual sign up response", _response);
    if (_response && _response.account_details && _response.access_token && _response.expires_in) {
      authContext.setCurrentUser(_response.account_details.email_address);
      authContext.storeLoginType(LoginType.email);
      authContext.storeAccessToken(_response.access_token);
      navigate("/");
    } else if (_response && _response.message === "Invalid Password") {
      setPasswordError(true);
      setPasswordErrorMessage("Invalid password.")
    } else {
      setEmailError(true);
      setEmailErrorMessage("Sign up failed for this email address.")
    }
  }

  return (
    <JunkyardCard variant="outlined">
      <Box sx={{ display: { xs: 'flex', md: 'flex' } }}>
        <Box
          component={"img"}
          src={OrangeTrieLogo}
          sx={{
            width: 240,
            height: 60,
            display: 'block',
            marginLeft: -10,
          }}
        />
      </Box>
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
      >
        Sign in
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
      >
        <FormControl>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextField
            error={emailError}
            helperText={emailErrorMessage}
            id="email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={emailError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <FormLabel htmlFor="password">Password</FormLabel>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'baseline' }}
            >
              Forgot your password?
            </Link>
          </Box>
          <TextField
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type="password"
            id="password"
            autoComplete="current-password"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label="Remember me"
        />
        <ForgotPassword open={open} handleClose={handleClose} />
        <TermsOfServiceDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          By using this service, you consent to the{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setTermsOpen(true)}
          >
            Terms of Service
          </Link>
          .
        </Typography>
        <Button type="submit" fullWidth variant="contained" onClick={handleManualSignIn}>
          Sign in
        </Button>
        <Typography sx={{ textAlign: 'center' }}>
          Don't have an account?{' '}
          <span>
            <Link
              href="#"
              variant="body2"
              sx={{ alignSelf: 'center' }}
              onClick={(event) => handleManualSignUp(event)}
            >
              Sign Up
            </Link>
          </span>
        </Typography>
      </Box>
      <Divider>or</Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleGithubSignIn}
          startIcon={<GithubIcon />}
        >
          Sign in with Github
        </Button>
        <Button
          fullWidth
          variant="outlined"
          // onClick={() => alert('Sign in with Google')}
          onClick={handleGoogleSignIn}
          startIcon={<GoogleIcon />}
        >
          Sign in with Google
        </Button>
        {/*<Button*/}
        {/*  fullWidth*/}
        {/*  variant="outlined"*/}
        {/*  onClick={() => alert('Sign in with Facebook')}*/}
        {/*  startIcon={<FacebookIcon />}*/}
        {/*>*/}
        {/*  Sign in with Facebook*/}
        {/*</Button>*/}
      </Box>
    </JunkyardCard>
  );
}